/**
 * userSettings — persönliche Einstellungen je Benutzer (z. B. Dashboard-Layout).
 *
 * Speicherort: Spalte `agents.einstellungen` (JSONB, Migration 20261103_01).
 * Fehlt die Spalte noch (Migration nicht ausgeführt), bleiben die Werte im
 * localStorage dieses Geräts erhalten und der Aufrufer bekommt
 * `persisted: 'local'` zurück, um das anzuzeigen. Es werden nie andere
 * Profilfelder geschrieben (nur `einstellungen`).
 */
import { base44 } from '@/api/base44Client';

export const USER_SETTINGS_VERSION = 1;
const CACHE_PREFIX = 'fc:einstellungen:';
const MISSING_COLUMN_RE = /einstellungen/i;

const isMissingColumnError = (err) => {
  const msg = err?.message || '';
  return MISSING_COLUMN_RE.test(msg) && /column|schema cache|does not exist/i.test(msg);
};

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

/**
 * @param {{ entity?: { get: Function, update: Function }, storage?: Storage|null }} deps
 */
export function createUserSettingsStore({ entity, storage } = {}) {
  const getEntity = () => entity || base44.entities.Agent;
  const getStorage = () => {
    if (storage !== undefined) return storage;
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
  };
  const cacheKey = (user) => `${CACHE_PREFIX}${user.id}`;

  const readCache = (user) => {
    const s = getStorage();
    if (!s) return null;
    try { return safeParse(s.getItem(cacheKey(user))); } catch { return null; }
  };
  const writeCache = (user, settings) => {
    const s = getStorage();
    if (!s) return;
    try { s.setItem(cacheKey(user), JSON.stringify(settings)); } catch { /* Speicher voll / privat — ignorieren */ }
  };

  /** @returns {Promise<object>} Einstellungen (leer, wenn nichts gespeichert) */
  async function load(user) {
    if (!user?.id) return {};
    try {
      const row = await getEntity().get(user.id);
      const settings = row?.einstellungen && typeof row.einstellungen === 'object' ? row.einstellungen : null;
      if (settings) {
        writeCache(user, settings);
        return settings;
      }
    } catch {
      // Profil nicht lesbar (offline, Rechte) → lokaler Stand
    }
    return readCache(user) || {};
  }

  /**
   * Patch (flach) in die bestehenden Einstellungen mischen und speichern.
   * @returns {Promise<{ settings: object, persisted: 'db'|'local' }>}
   */
  async function save(user, patch) {
    if (!user?.id) throw new Error('Kein Benutzer angemeldet.');
    const current = await load(user);
    const settings = { ...current, ...patch, version: USER_SETTINGS_VERSION };
    try {
      await getEntity().update(user.id, { einstellungen: settings });
      writeCache(user, settings);
      return { settings, persisted: 'db' };
    } catch (err) {
      if (!isMissingColumnError(err)) throw err;
      writeCache(user, settings);
      return { settings, persisted: 'local' };
    }
  }

  return { load, save };
}

export const userSettingsStore = createUserSettingsStore();
